import React, {createContext, forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import ConfirmDialog from './confirm-dialog';

export type Subtab = 'settings' | 'blocks' | 'exemptions' | 'report';
export type BlocksStatusFilter = 'ACTIVE' | 'RELEASED' | 'ALL';

export interface BlocksFilter {
  status: BlocksStatusFilter;
  customerId: number | null;
  deviceId: number | null;
}

// Calendar days as YYYY-MM-DD, compared against the local day of each run.
export interface Period {
  from: string;
  to: string;
  // The report shortcut that produced the period; absent for a custom one.
  preset?: string;
}

export const defaultBlocksFilter: BlocksFilter = {status: 'ACTIVE', customerId: null, deviceId: null};

interface BillingBlockContextValue {
  subtab: Subtab;
  setSubtab: (subtab: Subtab) => void;
  blocksFilter: BlocksFilter;
  setBlocksFilter: (filter: BlocksFilter) => void;
  period: Period | null;
  setPeriod: (period: Period | null) => void;
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
  // Runs proceed now, or after the user agrees to drop unsaved settings in the module's dialog.
  confirmDiscard: (proceed: () => void) => void;
}

export interface BillingBlockHandle {
  confirmDiscard: (proceed: () => void) => void;
}

const BillingBlockContext = createContext<BillingBlockContextValue | null>(null);

interface BillingBlockProviderProps {
  children: React.ReactElement;
}

const BillingBlockProvider = forwardRef<BillingBlockHandle, BillingBlockProviderProps>(({children}, ref) => {
  const intl = useIntl();
  const [subtab, setSubtab] = useState<Subtab>('settings');
  const [blocksFilter, setFilter] = useState(defaultBlocksFilter);
  const [period, setPeriodState] = useState<Period | null>(null);
  const [dirty, setDirtyState] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(false);

  const setBlocksFilter = useCallback((filter: BlocksFilter) => setFilter(filter), []);
  const setPeriod = useCallback((next: Period | null) => setPeriodState(next), []);

  const setDirty = useCallback((next: boolean) => {
    dirtyRef.current = next;
    setDirtyState(next);
  }, []);

  const confirmDiscard = useCallback((proceed: () => void) => {
    if (dirtyRef.current) setPending(() => proceed);
    else proceed();
  }, []);

  useImperativeHandle(ref, () => ({confirmDiscard}), [confirmDiscard]);

  const value = useMemo(() => ({
    subtab, setSubtab, blocksFilter, setBlocksFilter, period, setPeriod, dirty, setDirty, confirmDiscard,
  }), [subtab, blocksFilter, setBlocksFilter, period, setPeriod, dirty, setDirty, confirmDiscard]);

  return (
    <BillingBlockContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!pending}
        title={intl.formatMessage({id: 'billingBlockDiscardTitle'})}
        description={<p>{intl.formatMessage({id: 'billingBlockDiscardChanges'})}</p>}
        confirmLabel={intl.formatMessage({id: 'billingBlockDiscardConfirm'})}
        onConfirm={() => {
          const proceed = pending;
          setPending(null);
          proceed?.();
        }}
        onCancel={() => setPending(null)}
      />
    </BillingBlockContext.Provider>
  );
});

BillingBlockProvider.displayName = 'BillingBlockProvider';

export const useBillingBlock = () => {
  const value = useContext(BillingBlockContext);
  if (!value) throw new Error('useBillingBlock needs a BillingBlockProvider');
  return value;
};

export default BillingBlockProvider;
